import React, { useState } from 'react';
import { Calendar, DollarSign, Home, User, Download, Check } from 'lucide-react';

const isPeakSeason = (month, day) => {
  return (month === 0 && day <= 5) || 
         (month === 6 && day >= 15) || 
         (month === 7 && day <= 15) || 
         (month === 11 && day >= 20);
};

const ParadyseMVP = () => {
  const [currentView, setCurrentView] = useState('booking'); // Default to booking
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [hoveredDay, setHoveredDay] = useState(null);

  // Updated owner data for 2 fractions (88 days)
  const ownerData = {
    name: 'John Doe',
    propertyName: 'Lunara Villa G',
    fractionsHeld: 2,
    totalDaysEntitlement: 88, // 2 shares x 44 days
    peakDaysAllocated: 20, // 2 x 10 days
    standardDaysAllocated: 46, // 2 x 23 days
    peakDaysUsed: 7, // Current usage
    standardDaysUsed: 15, // Current usage
    peakDaysRemaining: 13,
    standardDaysRemaining: 31
  };

  const [selectedDates, setSelectedDates] = useState([]);
  const [isBooking, setIsBooking] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  // Add missing drag state variables
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const owners = [
    { id: 'john', name: 'John Doe', color: 'bg-green-200', textColor: 'text-green-900', fractions: 1 },
    { id: 'mary', name: 'Mary Smith', color: 'bg-blue-200', textColor: 'text-blue-900', fractions: 2 },
    { id: 'owen', name: 'Owen Wilson', color: 'bg-purple-200', textColor: 'text-purple-900', fractions: 2 },
    { id: 'mario', name: 'Mario Claudius', color: 'bg-indigo-200', textColor: 'text-indigo-900', fractions: 2 }
  ];

  // Booking data with owner assignments
  const bookings = {
    // John's bookings
    'john': [
      { start: new Date(2025, 0, 1), end: new Date(2025, 0, 7), type: 'owner' },
      { start: new Date(2025, 3, 15), end: new Date(2025, 3, 22), type: 'owner' },
      { start: new Date(2025, 9, 10), end: new Date(2025, 9, 17), type: 'owner' }
    ],
    // Mary's bookings  
    'mary': [
      { start: new Date(2025, 6, 15), end: new Date(2025, 6, 22), type: 'owner' },
      { start: new Date(2025, 7, 5), end: new Date(2025, 7, 12), type: 'owner' }
    ],
    // Owen's bookings
    'owen': [
      { start: new Date(2025, 2, 10), end: new Date(2025, 2, 17), type: 'owner' },
      { start: new Date(2025, 8, 1), end: new Date(2025, 8, 8), type: 'owner' }
    ],
    // Mario's bookings
    'mario': [
      { start: new Date(2025, 11, 20), end: new Date(2025, 11, 27), type: 'owner' },
      { start: new Date(2025, 5, 1), end: new Date(2025, 5, 8), type: 'owner' }
    ],
    // Airbnb bookings
    'airbnb': [
      { start: new Date(2025, 1, 14), end: new Date(2025, 1, 18), type: 'airbnb' },
      { start: new Date(2025, 4, 20), end: new Date(2025, 4, 25), type: 'airbnb' },
      { start: new Date(2025, 7, 25), end: new Date(2025, 7, 30), type: 'airbnb' },
      { start: new Date(2025, 10, 5), end: new Date(2025, 10, 10), type: 'airbnb' }
    ]
  };

  const getBookingForDate = (date) => {
    const bookingsForDate = [];
    Object.entries(bookings).forEach(([ownerId, ownerBookings]) => {
      ownerBookings.forEach(booking => {
        if (date >= booking.start && date <= booking.end) {
          bookingsForDate.push({ ownerId, ...booking });
        }
      });
    });
    return bookingsForDate;
  };

  const toggleDateSelection = (date) => {
    const dateString = date.toISOString().split('T')[0];
    setSelectedDates(prev => {
      if (prev.includes(dateString)) {
        return prev.filter(d => d !== dateString);
      } else {
        return [...prev, dateString].sort();
      }
    });
  };

  // Add missing selectDateRange function
  const selectDateRange = (startDate, endDate) => {
    const start = new Date(Math.min(startDate, endDate));
    const end = new Date(Math.max(startDate, endDate));
    const datesInRange = [];
    
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split('T')[0];
      datesInRange.push(dateString);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    setSelectedDates(prev => {
      const newDates = [...prev];
      datesInRange.forEach(date => {
        if (!newDates.includes(date)) {
          newDates.push(date);
        }
      });
      return newDates.sort();
    });
  };

  const getConsecutiveDaysCount = () => {
    if (selectedDates.length === 0) return 0;
    const sortedDates = selectedDates.sort();
    let maxConsecutive = 1;
    let currentConsecutive = 1;
    
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = new Date(sortedDates[i - 1]);
      const currentDate = new Date(sortedDates[i]);
      const dayDiff = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (dayDiff === 1) {
        currentConsecutive++;
        maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
      } else {
        currentConsecutive = 1;
      }
    }
    return maxConsecutive;
  };

  const getPeakDaysSelected = () => {
    return selectedDates.filter(dateStr => {
      const date = new Date(dateStr);
      return isPeakSeason(date.getMonth(), date.getDate());
    }).length;
  };

  const getStandardDaysSelected = () => {
    return selectedDates.length - getPeakDaysSelected();
  };

  const canBookSelection = () => {
    const peakDays = getPeakDaysSelected();
    const standardDays = getStandardDaysSelected();
    const consecutiveDays = getConsecutiveDaysCount();
    
    return peakDays <= ownerData.peakDaysRemaining && 
           standardDays <= ownerData.standardDaysRemaining &&
           consecutiveDays <= 14; // Max 14 consecutive days policy
  };

  const financialData = {
    grossRentalIncome: 68839,
    propertyMgmtExpenses: 10325.85,
    leasingMarketing: 10325.85,
    financialGovernance: 2200,
    netIncome: 45987.3,
    ownerShare: 5748.41,
    peakRevenue: 9700.625,
    standardRevenue: 22089.375
  };

  const Login = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Paradyse</h1>
          <p className="text-gray-600 mt-2">Co-ownership platform</p>
        </div>
        <input type="email" placeholder="Enter your email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4" />
        <button onClick={() => setCurrentView('booking')} className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-colors">
          Send Magic Link
        </button>
      </div>
    </div>
  );

  const Dashboard = () => (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900">Paradyse</span>
          </div>
          <div className="flex items-center space-x-6">
            <button onClick={() => setCurrentView('booking')} className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${currentView === 'booking' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
              <Calendar className="w-4 h-4" />
              <span>Booking</span>
            </button>
            <button onClick={() => setCurrentView('financial')} className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${currentView === 'financial' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
              <DollarSign className="w-4 h-4" />
              <span>Financial</span>
            </button>
            <User className="w-6 h-6 text-gray-400" />
          </div>
        </div>
      </nav>

      {currentView === 'booking' && <BookingView />}
      {currentView === 'financial' && <FinancialView />}
    </div>
  );

  const BookingView = () => (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Usage Allocation - {ownerData.name}</h2>
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="font-semibold text-orange-800 mb-3">Peak Days (22.7% of year)</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-orange-700">Allocated</span>
                <span className="font-medium">{ownerData.peakDaysAllocated} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-700">Used</span>
                <span className="font-medium">{ownerData.peakDaysUsed} days</span>
              </div>
              <hr className="border-orange-300" />
              <div className="flex justify-between font-bold">
                <span className="text-orange-800">Remaining</span>
                <span className="text-orange-800">{ownerData.peakDaysRemaining} days</span>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-3">Standard Days (51.8% of year)</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Allocated</span>
                <span className="font-medium">{ownerData.standardDaysAllocated} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Used</span>
                <span className="font-medium">{ownerData.standardDaysUsed} days</span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between font-bold">
                <span className="text-blue-800">Remaining</span>
                <span className="text-blue-800">{ownerData.standardDaysRemaining} days</span>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Controls */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-800">Booking Calendar</h3>
              <p className="text-sm text-blue-600">
                Click dates to select • Hold and drag to select multiple days • Peak: {ownerData.peakDaysRemaining} days • Standard: {ownerData.standardDaysRemaining} days remaining
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {selectedDates.length > 0 && (
                <div className="text-sm text-blue-700">
                  <span className="font-medium">{selectedDates.length} days selected</span>
                  <br />
                  <span>Peak: {getPeakDaysSelected()}, Standard: {getStandardDaysSelected()}</span>
                </div>
              )}
              {selectedDates.length > 0 && (
                <button
                  onClick={() => {
                    if (canBookSelection()) {
                      setShowBookingModal(true);
                    }
                  }}
                  className={`w-40 px-4 py-2 rounded-lg transition-colors ${
                    canBookSelection()
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : 'bg-red-500 text-white cursor-not-allowed'
                  }`}
                  disabled={!canBookSelection()}
                >
                  {canBookSelection() ? 'Book Selected Dates' : 'Invalid Selection'}
                </button>
              )}
              {selectedDates.length > 0 && (
                <button
                  onClick={() => setSelectedDates([])}
                  className="w-32 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Clear Selection
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-4 mb-4">
          <span className="text-sm font-medium text-gray-700">Show bookings:</span>
          <select 
            value={selectedOwner} 
            onChange={(e) => setSelectedOwner(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
          >
            <option value="all">All Owners</option>
            {owners.map(owner => (
              <option key={owner.id} value={owner.id}>{owner.name}</option>
            ))}
            <option value="airbnb">Airbnb Only</option>
          </select>
        </div>

        {/* Annual Booking Calendar - 3 months per row */}
        <h3 className="font-semibold text-gray-900 mb-4">2025 Annual Calendar - First Come, First Served</h3>
        <div className="space-y-6">
          {[0, 3, 6, 9].map((quarterStart) => (
            <div key={quarterStart} className="grid grid-cols-3 gap-6">
              {[0, 1, 2].map((monthOffset) => {
                const monthIndex = quarterStart + monthOffset;
                if (monthIndex >= 12) return null;
                const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex];
                const daysInMonth = new Date(2025, monthIndex + 1, 0).getDate();
                const firstDayOfWeek = new Date(2025, monthIndex, 1).getDay();
                
                return (
                  <div key={monthIndex} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 text-center">{monthName} 2025</h4>
                    <div className="grid grid-cols-7 gap-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 p-1">{day}</div>
                      ))}
                      
                      {/* Empty cells for days before month starts */}
                      {[...Array(firstDayOfWeek)].map((_, index) => (
                        <div key={`empty-${index}`} className="h-8"></div>
                      ))}
                      
                      {/* Month days */}
                      {[...Array(daysInMonth)].map((_, dayIndex) => {
                        const day = dayIndex + 1;
                        const currentDate = new Date(2025, monthIndex, day);
                        const dayBookings = getBookingForDate(currentDate);
                        const isPeak = isPeakSeason(monthIndex, day);
                        
                        // Filter bookings based on selected owner
                        const filteredBookings = selectedOwner === 'all' 
                          ? dayBookings 
                          : dayBookings.filter(b => b.ownerId === selectedOwner);
                        
                        const isBooked = filteredBookings.length > 0;
                        const booking = filteredBookings[0];
                        const dateString = currentDate.toISOString().split('T')[0];
                        const isSelected = selectedDates.includes(dateString);
                        
                        let bgColor = 'bg-gray-50 hover:bg-gray-100';
                        let textColor = 'text-gray-700';
                        let canClick = true; // Available for booking by default
                        
                        if (isSelected) {
                          // Selected dates show yellow
                          bgColor = 'bg-yellow-300 hover:bg-yellow-400';
                          textColor = 'text-yellow-900';
                        } else if (isBooked && booking) {
                          // Booked dates - cannot be selected
                          canClick = false;
                          if (booking.type === 'airbnb') {
                            bgColor = 'bg-red-300';
                            textColor = 'text-red-900';
                          } else {
                            const owner = owners.find(o => o.id === booking.ownerId);
                            if (owner) {
                              bgColor = owner.color;
                              textColor = owner.textColor;
                            }
                          }
                        } else if (isPeak) {
                          // Peak season available dates
                          bgColor = 'bg-orange-100 hover:bg-orange-200';
                          textColor = 'text-orange-800';
                        }
                        // Standard available dates keep default grey styling
                        
                        return (
                          <button
                            key={dayIndex}
                            onClick={() => canClick && toggleDateSelection(currentDate)}
                            onMouseDown={() => canClick && setDragStart(currentDate)}
                            onMouseEnter={() => {
                              setHoveredDay({ date: currentDate, bookings: dayBookings });
                              if (dragStart && canClick) {
                                setIsDragging(true);
                              }
                            }}
                            onMouseUp={() => {
                              if (dragStart && isDragging && canClick) {
                                selectDateRange(dragStart, currentDate);
                                setDragStart(null);
                                setIsDragging(false);
                              }
                            }}
                            onMouseLeave={() => setHoveredDay(null)}
                            className={`h-8 w-8 rounded text-xs transition-colors relative ${bgColor} ${textColor} ${
                              canClick ? 'cursor-pointer' : 'cursor-not-allowed'
                            } select-none`}
                          >
                            {day}
                            {dayBookings.length > 1 && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full text-white text-xs flex items-center justify-center">
                                {dayBookings.length}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoveredDay && hoveredDay.bookings.length > 0 && (
          <div className="fixed z-50 bg-black text-white text-xs rounded p-2 pointer-events-none" style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <div className="font-medium">{hoveredDay.date.toDateString()}</div>
            {hoveredDay.bookings.map((booking, index) => (
              <div key={index}>
                {booking.type === 'airbnb' ? 'Airbnb Guest' : owners.find(o => o.id === booking.ownerId)?.name}
              </div>
            ))}
          </div>
        )}
        
        {/* Booking Confirmation Modal */}
        {showBookingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="font-bold text-gray-900 mb-4">Confirm Booking Request</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Selected Dates:</p>
                  <p className="font-medium">{selectedDates.length} days total</p>
                  <p className="text-xs text-gray-500">
                    {selectedDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                    {selectedDates.length > 3 && ` and ${selectedDates.length - 3} more...`}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Peak Days:</p>
                    <p className="font-medium">{getPeakDaysSelected()} days</p>
                    <p className="text-xs text-gray-500">Remaining: {ownerData.peakDaysRemaining - getPeakDaysSelected()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Standard Days:</p>
                    <p className="font-medium">{getStandardDaysSelected()} days</p>
                    <p className="text-xs text-gray-500">Remaining: {ownerData.standardDaysRemaining - getStandardDaysSelected()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Max Consecutive:</p>
                  <p className="font-medium">{getConsecutiveDaysCount()} days</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    ✓ Booking follows first-come, first-served policy<br/>
                    ✓ Request will be processed within 24 hours<br/>
                    ✓ You'll receive confirmation via email
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowBookingModal(false);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowBookingModal(false);
                      setIsBooking(false);
                      setSelectedDates([]);
                      // Here you would submit the booking request
                    }}
                    className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Submit Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-200 rounded"></div>
              <span className="text-gray-600">John Doe (You) - 2 shares</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-200 rounded"></div>
              <span className="text-gray-600">Mary Smith - 2 shares</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-purple-200 rounded"></div>
              <span className="text-gray-600">Owen Wilson - 2 shares</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-indigo-200 rounded"></div>
              <span className="text-gray-600">Mario Claudius - 2 shares</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-300 rounded"></div>
              <span className="text-gray-600">Airbnb Guests</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-300 rounded"></div>
              <span className="text-gray-600">Selected</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-100 rounded"></div>
              <span className="text-gray-600">Peak season</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Usage Policy Summary</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm text-gray-700">
          <p>• <strong>Total Annual Entitlement:</strong> {ownerData.totalDaysEntitlement} days per 1/8th share</p>
          <p>• <strong>First-Come, First-Served:</strong> Bookings processed in order received with waitlist system</p>
          <p>• <strong>Maintenance Reserve:</strong> 13 days annually reserved for property maintenance</p>
          <p>• <strong>Income Option:</strong> Unused days can be rented via professional management</p>
        </div>
      </div>
    </div>
  );

  const FinancialView = () => (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Financial Report - {ownerData.propertyName}</h2>
        <button className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
          <Download className="w-4 h-4" />
          <span>Export Report</span>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <h3 className="font-semibold text-green-800 mb-4">Revenue</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-green-700">Gross Rental Income</span>
              <span className="font-medium">${financialData.grossRentalIncome.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-700">Peak Season Revenue</span>
              <span className="font-medium">${financialData.peakRevenue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-700">Standard Season Revenue</span>
              <span className="font-medium">${financialData.standardRevenue.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="font-semibold text-red-800 mb-4">Operating Expenses</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-red-700">Property Management (15%)</span>
              <span className="font-medium">${financialData.propertyMgmtExpenses.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-700">Marketing & Leasing (15%)</span>
              <span className="font-medium">${financialData.leasingMarketing.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-700">Financial & Governance</span>
              <span className="font-medium">${financialData.financialGovernance.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-800 mb-4">Your Share (1/8)</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-700">Net Property Income</span>
              <span className="font-medium">${financialData.netIncome.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Your Share (12.5%)</span>
              <span className="font-bold text-lg text-green-600">${financialData.ownerShare.toLocaleString()}</span>
            </div>
            <div className="bg-blue-100 rounded p-2 mt-3">
              <div className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-700">Quarterly distribution</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Paradyse Financial Model</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p><strong>Revenue Model:</strong> Professional short-term rental management with 82% average occupancy</p>
          <p><strong>Cost Structure:</strong> 15% property management + 15% marketing + fixed governance costs</p>
          <p><strong>Profit Distribution:</strong> Net income shared proportionally among 8 co-owners</p>
          <p><strong>Usage vs Income:</strong> Days can be used personally or rented for additional income</p>
        </div>
      </div>
    </div>
  );

  if (currentView === 'login') return <Login />;
  return <Dashboard />;
};

export default ParadyseMVP;